<template>
  <div>
    <!-- Modal Trigger Button -->
    <b-button @click="showModal = true" variant="primary" class="add-resource-btn">
      <feather-icon icon="PlusIcon" size="16" />
    </b-button>

    <!-- Create UOM Modal -->
    <b-modal
      v-model="showModal"
      title="Create New Unit of Measure"
      size="xl"
      :hide-footer="true"
      :hide-header="true"
      :hide-header-close="true"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      centered
      @hidden="onModalHidden"
      id="create-uom-modal"
    >
      <!-- Modal Body -->
      <div class="modal-body p-0">
        <UOMForm
          :form-data="uomFormData"
          :loading="loading"
          :modal-mode="true"
          @submit="handleSubmit"
          @update:form-data="updateFormData"
          @cancel="closeModal"
          :is-modal="true"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue';
import { ref, reactive, computed } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import UOMForm from './UOMForm.vue';
import store from '@/store';
import UOMStoreModule from './UOMStoreModule.js';

export default {
  name: 'CreateUOMModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    UOMForm,
    ToastificationContent,
  },
  emits: ['submit'],
  setup(props, { emit }) {
    const toast = useToast();
    const businessID = computed(() => store.state.user.businessID);

    // Register UOM store module if not already registered
    const UOM_STORE_MODULE_NAME = 'uom';
    if (!store.hasModule(UOM_STORE_MODULE_NAME)) {
      store.registerModule(UOM_STORE_MODULE_NAME, UOMStoreModule);
    }

    // Reactive state
    const showModal = ref(false);
    const loading = ref(false);
    const uomFormData = reactive({
      id: 0,
      name: '',
      abbreviation: '',
      description: '',
      isActive: true,
      businessID: businessID.value,
    });

    // Methods
    const updateFormData = (newData) => {
      Object.assign(uomFormData, newData);
    };

    const handleSubmit = async () => {
      // Validate required fields
      if (!uomFormData.name || !uomFormData.abbreviation) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fill in all required fields',
            variant: 'warning',
            icon: 'AlertTriangleIcon',
          },
        });
        return;
      }

      loading.value = true;

      try {
        // Call the store action to create UOM
        const uomResponse = await store.dispatch('uom/addUOM', uomFormData);

        // Show success message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Unit of Measure created successfully!',
            variant: 'success',
            icon: 'CheckIcon',
          },
        });

        // Reset form data
        resetFormData();

        // Close modal
        closeModal();

        // Emit submit event to parent
        emit('submit', uomResponse);
      } catch (error) {
        console.error('Error creating UOM:', error);

        // Show error message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text:
              error?.response?.data?.message ||
              'Failed to create Unit of Measure. Please try again.',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const resetFormData = () => {
      Object.assign(uomFormData, {
        id: 0,
        name: '',
        abbreviation: '',
        description: '',
        isActive: true,
        businessID: businessID.value,
      });
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const onModalHidden = () => {
      // Reset form when modal is hidden
      resetFormData();
    };

    return {
      // Reactive state
      showModal,
      loading,
      uomFormData,

      // Methods
      updateFormData,
      handleSubmit,
      resetFormData,
      closeModal,
      onModalHidden,
    };
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

::v-deep .modal-xl {
  max-width: 95%;
}

::v-deep .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
</style>
